import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import clienteService from '../../services/clienteService';

interface Cliente {
    nroCliente: number;
    nombre: string;
    razonSocial?: string;
    direccion?: string;
    telefono1?: string;
    email?: string;
    cuit?: string;
    tipoIva?: string;
}

interface ClienteSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (cliente: Cliente) => void;
}

const ClienteSearch: React.FC<ClienteSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState<boolean>(false);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (verTodos) setVerTodos(false);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            // Si es numérico puro, buscar directamente por nroCliente
            if (compact !== '' && /^\d+$/.test(compact)) {
                try {
                    const cliente = await clienteService.getCliente(Number(compact));
                    setClientes(cliente ? [cliente] : []);
                    if (!cliente) setError('No se encontraron clientes.');
                } catch (err: any) {
                    setClientes([]);
                    setError('No se encontraron clientes.');
                } finally {
                    setLoading(false);
                }
                return;
            }

            const response = await clienteService.getClientes({
                searchTerm: trimmed || undefined,
                pageNumber: 1,
                pageSize: 20  // Límite del backend (1-20)
            });
            setClientes(response.items || []);
            if (!response.items || response.items.length === 0) {
                setError('No se encontraron clientes.');
            }
        } catch (err: any) {
            setError(`Error al buscar clientes: ${err.message || 'Error desconocido'}`);
            setClientes([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, verTodos]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            const pageSize = 20; // Límite del backend
            const first = await clienteService.getClientes({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize
            });

            let all: Cliente[] = first.items || [];
            // Mostrar inmediatamente la primera página para mejorar percepción de velocidad
            setClientes(all);
            const totalPages = first.totalPages || 1;

            if (totalPages > 1) {
                const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                const promises = pages.map((page) =>
                    clienteService
                        .getClientes({ searchTerm: undefined, pageNumber: page, pageSize })
                        .then((resp) => {
                            if (resp && Array.isArray(resp.items)) {
                                const items = resp.items as Cliente[];
                                // Append incremental para mejorar percepción de velocidad
                                setClientes((prev) => prev.concat(items));
                                all = all.concat(items);
                            }
                        })
                        .catch(() => {})
                );
                await Promise.all(promises);
            }
            if (all.length === 0) {
                setError('No se encontraron clientes.');
            }
        } catch (err: any) {
            setError(`Error al cargar clientes: ${err.message || 'Error desconocido'}`);
            setClientes([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (cliente: Cliente) => {
        onSelect(cliente);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setClientes([]);
        setError(null);
        onClose();
    };

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setClientes([]);
            setError(null);
            return;
        }

        // Si se está mostrando "Ver Todos", evitamos la búsqueda automática
        // para no sobreescribir la lista completa con solo la primera página.
        if (verTodos) {
            return;
        }

        const handle = setTimeout(() => {
            // Ejecuta búsqueda automática mientras se escribe
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch, verTodos]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Cliente
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Nombre, razón social, CUIT o N° de cliente"
                        inputProps={{ 'aria-label': 'Buscar cliente' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleSearch}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button 
                            variant={verTodos ? 'contained' : 'text'} 
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {clientes.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {clientes.map((cliente) => (
                                        <Card 
                                            key={cliente.nroCliente} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(cliente)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Nro. Cliente: {cliente.nroCliente}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(cliente);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {cliente.nombre}
                                                        </Typography>
                                                    </Box>
                                                    {cliente.razonSocial && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                Razón Social
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {cliente.razonSocial}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {cliente.cuit && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                CUIT
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {cliente.cuit}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Nro. Cliente</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Razón Social</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>CUIT</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clientes.map((cliente) => (
                                            <TableRow 
                                                key={cliente.nroCliente} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(cliente)}
                                            >
                                                <TableCell>{cliente.nroCliente}</TableCell>
                                                <TableCell>{cliente.nombre}</TableCell>
                                                <TableCell>{cliente.razonSocial || '-'}</TableCell>
                                                <TableCell>{cliente.cuit || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(cliente);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && clientes.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron clientes
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClienteSearch;
